import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { inr } from "@/theme";
import { useAdminTheme } from "@/admin/AdminTheme";
import { ACard, StatCard, Badge, STATUS_TONE, AdminPageTitle } from "@/admin/ui";
import { Spinner } from "@/components/common";

export default function AdminDashboard() {
  const { c } = useAdminTheme();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api("/admin/analytics").then(setData).catch(() => setData(null)); }, []);
  if (!data) return <Spinner full />;

  const t = data.totals;
  const maxRev = Math.max(1, ...data.revenue_trend_30d.map((d) => d.revenue));

  return (
    <div className="fade-up">
      <AdminPageTitle title="Dashboard" subtitle="Live performance across web & mobile" />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 22 }}>
        <StatCard label="Revenue" value={inr(t.revenue)} sub={`${t.paid_orders} paid orders`} />
        <StatCard label="Orders" value={t.orders} />
        <StatCard label="Customers" value={t.users} />
        <StatCard label="Products" value={t.products} />
        <StatCard label="Avg Order" value={inr(t.paid_orders ? Math.round(t.revenue / t.paid_orders) : 0)} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16 }}>
        <ACard>
          <h3 className="font-display" style={{ margin: "0 0 16px", fontSize: 19, fontWeight: 700, color: c.onSurface }}>Revenue · Last 30 days</h3>
          {data.revenue_trend_30d.length === 0 ? <p style={{ color: c.mutedText }}>No revenue yet.</p> : (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 140 }}>
              {data.revenue_trend_30d.map((d) => (
                <div key={d.date} title={`${d.date}: ${inr(d.revenue)}`} style={{ flex: 1, minWidth: 4, background: c.primary, height: `${Math.max(4, (d.revenue / maxRev) * 100)}%`, borderRadius: "4px 4px 0 0", opacity: 0.85 }} />
              ))}
            </div>
          )}
        </ACard>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16 }}>
          <ACard>
            <h3 className="font-display" style={{ margin: "0 0 14px", fontSize: 19, fontWeight: 700 }}>Recent Orders</h3>
            {data.recent_orders.length === 0 ? <p style={{ color: c.mutedText }}>No orders yet.</p> : data.recent_orders.map((o) => (
              <div key={o.id} onClick={() => navigate(`/admin/order/${o.id}`)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${c.divider}`, cursor: "pointer" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>#{o.id.slice(0, 8)}</div>
                  <div style={{ fontSize: 12, color: c.mutedText }}>{o.shipping_name}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontWeight: 700 }}>{inr(o.amount)}</span>
                  <Badge tone={STATUS_TONE[o.status]}>{o.status}</Badge>
                </div>
              </div>
            ))}
          </ACard>

          <ACard>
            <h3 className="font-display" style={{ margin: "0 0 14px", fontSize: 19, fontWeight: 700 }}>Top Sellers</h3>
            {data.top_products.length === 0 ? <p style={{ color: c.mutedText }}>No sales yet.</p> : data.top_products.map((p) => (
              <div key={p.product_id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${c.divider}` }}>
                {p.image && <img src={p.image} alt="" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "cover" }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="clamp-1" style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: c.mutedText }}>{p.units_sold} sold</div>
                </div>
                <span style={{ fontWeight: 700 }}>{inr(p.revenue)}</span>
              </div>
            ))}
          </ACard>
        </div>

        {data.low_stock.length > 0 && (
          <ACard style={{ borderColor: c.warning }}>
            <h3 className="font-display" style={{ margin: "0 0 14px", fontSize: 19, fontWeight: 700, color: c.warning }}>Low Stock Alert</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {data.low_stock.map((p) => (
                <div key={p.id} onClick={() => navigate("/admin/inventory")} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: c.warningSoft, borderRadius: 10, cursor: "pointer" }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</span>
                  <Badge tone="warning">{p.stock} left</Badge>
                </div>
              ))}
            </div>
          </ACard>
        )}
      </div>
    </div>
  );
}
